import React from "react";
import { Link } from "react-router-dom";
import { Star, ArrowLeft, Phone, Mail, MessageCircle, CheckCircle2 } from "lucide-react";
import axios from "axios";
import SiteNavbar from "@/components/SiteNavbar";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";
import { CONTACT } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [sent, setSent] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [form, setForm] = React.useState({
    name: "",
    business: "",
    phone: "",
    message: "",
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      // Fire Meta Pixel Lead conversion event
      if (typeof window !== "undefined" && window.fbq) {
        window.fbq("track", "Lead");
      }
      setSent(true);
    } catch (err) {
      setError(
        "Sorry, something went wrong sending your request. Please try again, or call us directly."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1f3d] text-white">
      <SiteNavbar />

      <main className="mx-auto max-w-7xl px-5 py-14 sm:px-8">
        <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-sm text-white/80">
          <Star className="h-4 w-4 text-[#e9a31e]" />
          Free Digital Audit
        </div>

        <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-6xl">
          Get in <span className="text-[#e9a31e]">Touch</span>
        </h1>
        <p className="mt-4 max-w-2xl text-lg text-white/70">
          30 minutes. We map out your online presence, spot the gaps, and show
          you exactly what we can do. No obligation, no pushy pitch.
        </p>

        <Reveal>
        <div className="mt-12 grid gap-10 lg:grid-cols-[1.4fr_1fr]">
          {/* Form */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
            {sent ? (
              <div className="flex min-h-[320px] flex-col items-center justify-center text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#e9a31e]/15 text-[#e9a31e]">
                  <CheckCircle2 className="h-8 w-8" />
                </div>
                <h3 className="mt-5 text-2xl font-bold">
                  Thanks — request received!
                </h3>
                <p className="mt-2 max-w-sm text-white/70">
                  Your free audit request is on its way to our team. We’ll be in
                  touch shortly to book your 30-minute session.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <Field
                  label="Your name"
                  placeholder="Jane Smith"
                  required
                  value={form.name}
                  onChange={update("name")}
                />
                <Field
                  label="Business name"
                  placeholder="Acme Ltd"
                  required
                  value={form.business}
                  onChange={update("business")}
                />
                <Field
                  label="Phone number"
                  type="tel"
                  placeholder="07…"
                  required
                  value={form.phone}
                  onChange={update("phone")}
                />
                <div>
                  <label className="mb-2 block text-sm font-medium text-white/80">
                    Tell us about your business{" "}
                    <span className="text-white/40">(optional)</span>
                  </label>
                  <textarea
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="What do you do, and what would you love to improve?"
                    className="w-full rounded-lg border border-white/15 bg-[#081831] px-4 py-3 text-white placeholder:text-white/35 outline-none transition-colors focus:border-[#e9a31e]"
                  />
                </div>
                {error && (
                  <p className="text-sm font-medium text-red-400">{error}</p>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full rounded-lg bg-[#e9a31e] px-6 py-3.5 text-base font-bold text-[#0a1f3d] transition-colors hover:bg-[#f2b743] disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {submitting ? "Sending…" : "Book My Free Audit"}
                </button>
              </form>
            )}
          </div>

          {/* Contact details */}
          <div className="space-y-4">
            <a href={`tel:${CONTACT.phoneRaw}`}>
              <InfoCard
                icon={<Phone className="h-5 w-5" />}
                title="Call us"
                value={CONTACT.phone}
              />
            </a>
            <a href={`mailto:${CONTACT.email}`}>
              <InfoCard
                icon={<Mail className="h-5 w-5" />}
                title="Email"
                value={CONTACT.email}
              />
            </a>
            <a href={CONTACT.whatsapp} target="_blank" rel="noopener noreferrer">
              <InfoCard
                icon={<MessageCircle className="h-5 w-5" />}
                title="WhatsApp"
                value="Message us on WhatsApp"
              />
            </a>
          </div>
        </div>
        </Reveal>
      </main>

      <Footer />
    </div>
  );
}

function Field({ label, type = "text", placeholder, required, value, onChange }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-white/80">
        {label}
        {required && <span className="text-[#e9a31e]"> *</span>}
      </label>
      <input
        type={type}
        placeholder={placeholder}
        required={required}
        value={value}
        onChange={onChange}
        className="w-full rounded-lg border border-white/15 bg-[#081831] px-4 py-3 text-white placeholder:text-white/35 outline-none transition-colors focus:border-[#e9a31e]"
      />
    </div>
  );
}

function InfoCard({ icon, title, value }) {
  return (
    <div className="flex items-center gap-4 rounded-xl border border-white/10 bg-white/[0.03] p-5 transition-colors hover:border-[#e9a31e]/40">
      <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-[#e9a31e]/15 text-[#e9a31e]">
        {icon}
      </div>
      <div>
        <p className="text-sm text-white/55">{title}</p>
        <p className="font-semibold break-all">{value}</p>
      </div>
    </div>
  );
}
